import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import {
  fetchStoreProducts,
  getProductDisplayPrice,
  getProductMrp,
  isProductOutOfStock,
  snapshotStoreProduct,
} from '../utils/products';
import {
  StoreCartError,
  addGuestCartLine,
  addProductToCart,
  clearStoreCartItems,
  fetchStoreCartItems,
  readGuestCartItems,
  removeGuestCartLine,
  removeProductFromCart,
  setGuestCartLineQuantity,
  sameProductId,
  syncGuestCartToApi,
  writeGuestCartItems,
} from '../utils/storeCart';

const StoreCartContext = createContext({
  items: [],
  itemCount: 0,
  subtotal: 0,
  mrpTotal: 0,
  savings: 0,
  loading: false,
  updating: false,
  isGuest: true,
  addItem: async () => {},
  setQuantity: async () => {},
  removeItem: async () => {},
  clearCart: async () => {},
  refreshCart: async () => {},
  hydrateItems: () => [],
  hydratedItems: [],
  isDrawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
  catalogProducts: [],
  setCatalogProducts: () => {},
  checkoutItems: [],
  checkoutOpen: false,
  openCheckout: () => {},
  closeCheckout: () => {},
});

const AUTH_CART_QTY_STORAGE_KEY = 'daiv_store_cart_qty';

function readRememberedQuantities() {
  try {
    const raw = localStorage.getItem(AUTH_CART_QTY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeRememberedQuantities(map) {
  try {
    localStorage.setItem(AUTH_CART_QTY_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

function rememberLineQuantities(items) {
  const current = readRememberedQuantities();
  for (const row of items || []) {
    const id = String(row?.productId || '');
    const qty = Number(row?.quantity);
    if (id && Number.isFinite(qty) && qty > 0) current[id] = qty;
  }
  writeRememberedQuantities(current);
}

function applyRememberedQuantities(items) {
  const remembered = readRememberedQuantities();
  return (items || []).map((row) => {
    const id = String(row?.productId || '');
    const rememberedQty = Number(remembered[id]);
    const apiQty = Number(row?.quantity);
    const quantity =
      Number.isFinite(rememberedQty) && rememberedQty > 0
        ? Math.max(rememberedQty, Number.isFinite(apiQty) ? apiQty : 0)
        : Number.isFinite(apiQty) && apiQty > 0
          ? apiQty
          : 1;
    return { ...row, productId: id, quantity };
  });
}

function lineTotals(product, quantity) {
  const qty = Math.max(1, Number(quantity) || 1);
  const price = getProductDisplayPrice(product) || 0;
  const mrp = getProductMrp(product) || price;
  return {
    subtotal: price * qty,
    mrpTotal: mrp * qty,
    savings: Math.max(0, (mrp - price) * qty),
  };
}

function mergeProductSnapshot(catalogProduct, row, lang = 'en') {
  const snapshot = snapshotStoreProduct(catalogProduct, lang);
  if (!snapshot) return row.product;
  const merged = {
    ...row.product,
    ...snapshot,
    id: row.productId,
    maxQuantity: Math.max(
      Number(row.product?.maxQuantity) || 0,
      Number(snapshot.maxQuantity) || 0,
      99
    ),
  };
  if (getProductDisplayPrice(merged) == null && getProductDisplayPrice(catalogProduct) != null) {
    merged.finalSellingPrice =
      catalogProduct.finalSellingPrice ??
      catalogProduct.final_selling_price ??
      catalogProduct.unitPrice ??
      catalogProduct.unit_price;
    merged.sellingPrice =
      catalogProduct.sellingPrice ?? catalogProduct.selling_price ?? catalogProduct.price;
  }
  return merged;
}

export function StoreCartProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { lang } = useLanguage();
  const [items, setItems] = useState(() => readGuestCartItems());
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [checkoutItems, setCheckoutItems] = useState([]);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const loginSyncDoneRef = useRef(false);

  const lineMaxQuantity = useCallback(() => 99, []);

  const updateGuestItems = useCallback((updater) => {
    setItems((prev) => {
      const next = updater(prev);
      writeGuestCartItems(next);
      return next;
    });
  }, []);

  const refreshCart = useCallback(async () => {
    if (!isAuthenticated) {
      setItems(readGuestCartItems());
      return;
    }
    setLoading(true);
    try {
      const list = await fetchStoreCartItems();
      const merged = applyRememberedQuantities(list);
      rememberLineQuantities(merged);
      setItems(merged);
    } catch (err) {
      if (err instanceof StoreCartError && err.code === 'LOGIN_REQUIRED') {
        setItems(readGuestCartItems());
      } else {
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      loginSyncDoneRef.current = false;
      setItems(readGuestCartItems());
      return;
    }

    if (loginSyncDoneRef.current) {
      refreshCart();
      return;
    }

    let cancelled = false;
    loginSyncDoneRef.current = true;

    (async () => {
      const guestItems = readGuestCartItems();
      if (guestItems.length) {
        rememberLineQuantities(guestItems);
        setUpdating(true);
        try {
          await syncGuestCartToApi(guestItems);
          writeGuestCartItems([]);
        } catch {
          // Keep guest cart if sync fails; user can retry after refresh
        } finally {
          if (!cancelled) setUpdating(false);
        }
      }
      if (!cancelled) await refreshCart();
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, refreshCart]);

  const addItem = useCallback(
    async (product, quantity = 1) => {
      const snapshot = snapshotStoreProduct(product, lang);
      if (!snapshot || isProductOutOfStock(product)) return;

      const addQty = Math.max(1, Math.min(Number(quantity) || 1, snapshot.maxQuantity || 99));
      const productId = String(snapshot.id);

      if (!isAuthenticated) {
        updateGuestItems((prev) => addGuestCartLine(prev, snapshot, addQty));
        return;
      }

      const existing = items.find((item) => sameProductId(item.productId, productId));
      const nextQty = Math.min(99, (Number(existing?.quantity) || 0) + addQty);
      rememberLineQuantities([{ productId, quantity: nextQty }]);

      setUpdating(true);
      try {
        if (!existing) await addProductToCart(productId);
        await refreshCart();
      } finally {
        setUpdating(false);
      }
    },
    [isAuthenticated, items, updateGuestItems, refreshCart, lang]
  );

  const setQuantity = useCallback(
    async (productId, quantity) => {
      const id = String(productId || '');
      if (!id) return;

      if (!isAuthenticated) {
        updateGuestItems((prev) => {
          const row = prev.find((item) => sameProductId(item.productId, id));
          if (!row) return prev;
          const max = lineMaxQuantity();
          const target = Math.min(max, Math.max(0, Number(quantity) || 0));
          return setGuestCartLineQuantity(prev, id, target);
        });
        return;
      }

      const row = items.find((item) => sameProductId(item.productId, id));
      if (!row) return;
      const max = lineMaxQuantity();
      const target = Math.min(max, Math.max(0, Number(quantity) || 0));
      if (target === Number(row.quantity)) return;

      if (target <= 0) {
        setUpdating(true);
        try {
          await removeProductFromCart(id);
          const remaining = readRememberedQuantities();
          delete remaining[id];
          writeRememberedQuantities(remaining);
          await refreshCart();
        } finally {
          setUpdating(false);
        }
        return;
      }

      rememberLineQuantities([{ productId: id, quantity: target }]);
      setItems((prev) =>
        prev.map((item) => (sameProductId(item.productId, id) ? { ...item, quantity: target } : item))
      );
    },
    [isAuthenticated, items, updateGuestItems, refreshCart, lineMaxQuantity]
  );

  const removeItem = useCallback(
    async (productId) => {
      if (!isAuthenticated) {
        updateGuestItems((prev) => removeGuestCartLine(prev, productId));
        return;
      }

      const id = String(productId || '');
      setUpdating(true);
      try {
        await removeProductFromCart(id);
        const remaining = readRememberedQuantities();
        delete remaining[id];
        writeRememberedQuantities(remaining);
        await refreshCart();
      } finally {
        setUpdating(false);
      }
    },
    [isAuthenticated, updateGuestItems, refreshCart]
  );

  const clearCart = useCallback(async () => {
    if (!isAuthenticated) {
      updateGuestItems(() => []);
      return;
    }

    const ids = items.map((row) => row.productId);
    if (!ids.length) {
      setItems([]);
      return;
    }
    setUpdating(true);
    try {
      await clearStoreCartItems(ids);
      writeRememberedQuantities({});
      setItems([]);
    } finally {
      setUpdating(false);
    }
  }, [isAuthenticated, items, updateGuestItems]);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const openCheckout = useCallback((items) => {
    setCheckoutItems(Array.isArray(items) ? items : []);
    setCheckoutOpen(true);
    setIsDrawerOpen(false);
  }, []);

  const closeCheckout = useCallback(() => {
    setCheckoutOpen(false);
    setCheckoutItems([]);
  }, []);

  const hydrateItems = useCallback((catalogProducts, cartItems) => {
    if (!catalogProducts?.length) return cartItems;
    const byId = new Map(catalogProducts.map((p) => [String(p.id), p]));
    return cartItems.map((row) => {
      const catalog = byId.get(String(row.productId));
      if (!catalog) return row;
      return { ...row, product: mergeProductSnapshot(catalog, row, lang) };
    });
  }, [lang]);

  useEffect(() => {
    if (catalogProducts.length > 0) return undefined;
    if (items.length === 0 && !isDrawerOpen) return undefined;

    let cancelled = false;
    fetchStoreProducts(lang)
      .then((products) => {
        if (!cancelled && products.length) setCatalogProducts(products);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [catalogProducts.length, items.length, isDrawerOpen, lang]);

  const hydratedItems = useMemo(
    () => hydrateItems(catalogProducts, items),
    [hydrateItems, catalogProducts, items]
  );

  const totals = useMemo(() => {
    return hydratedItems.reduce(
      (acc, row) => {
        const t = lineTotals(row.product, row.quantity);
        acc.subtotal += t.subtotal;
        acc.mrpTotal += t.mrpTotal;
        acc.savings += t.savings;
        return acc;
      },
      { subtotal: 0, mrpTotal: 0, savings: 0 }
    );
  }, [hydratedItems]);

  const itemCount = useMemo(
    () => items.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      itemCount,
      subtotal: totals.subtotal,
      mrpTotal: totals.mrpTotal,
      savings: totals.savings,
      loading,
      updating,
      isGuest: !isAuthenticated,
      addItem,
      setQuantity,
      removeItem,
      clearCart,
      refreshCart,
      hydrateItems,
      hydratedItems,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      catalogProducts,
      setCatalogProducts,
      checkoutItems,
      checkoutOpen,
      openCheckout,
      closeCheckout,
    }),
    [
      items,
      itemCount,
      totals,
      loading,
      updating,
      isAuthenticated,
      addItem,
      setQuantity,
      removeItem,
      clearCart,
      refreshCart,
      hydrateItems,
      hydratedItems,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      catalogProducts,
      checkoutItems,
      checkoutOpen,
      openCheckout,
      closeCheckout,
    ]
  );

  return <StoreCartContext.Provider value={value}>{children}</StoreCartContext.Provider>;
}

export function useStoreCart() {
  return useContext(StoreCartContext);
}

export { StoreCartError };
